import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

function DownloadsList({ downloads }) {
  return (
    <div className="space-y-6">
      {downloads.map((d) => (
        <div
          key={d.product_id}
          data-testid={`download-${d.product_id}`}
          className="border border-mizizi-gold/30 bg-mizizi-cream p-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="overline text-mizizi-terracotta">{d.product_id}</div>
              <h2 className="font-serif text-3xl mt-2 text-mizizi-charcoal">{d.name}</h2>
              <p className="mt-2 text-sm text-mizizi-charcoal/60">Links refresh every {d.expires_in_days} days · Signed to your inbox</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href={d.print_url}
                data-testid={`download-${d.product_id}-print`}
                className="pill pill-primary"
                download
              >
                Print PDF
              </a>
              <a
                href={d.mobile_url}
                data-testid={`download-${d.product_id}-mobile`}
                className="pill pill-outline"
                download
              >
                Mobile PDF
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <p className="font-serif text-2xl text-mizizi-charcoal">No downloads yet.</p>
      <Link to="/shop" data-testid="downloads-shop" className="pill pill-primary mt-8 inline-flex">Explore the Editions</Link>
    </div>
  );
}

function LoadingState() {
  return <div className="text-center font-serif text-mizizi-charcoal/60 py-16">Preparing your downloads…</div>;
}

function renderDownloads(loading, downloads) {
  if (loading) return <LoadingState />;
  if (downloads.length === 0) return <EmptyState />;
  return <DownloadsList downloads={downloads} />;
}

export default function Downloads() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    client
      .get("/downloads/me")
      .then(({ data }) => setDownloads(data.downloads || []))
      .catch(() => toast.error("Could not load your downloads."))
      .finally(() => setLoading(false));
  }, [navigate]);

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>
      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-12 text-center">
        <div className="overline text-mizizi-gold">Downloads</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-none text-mizizi-charcoal">Take it with you.</h1>
        <p className="mt-6 max-w-2xl mx-auto text-mizizi-charcoal/70 text-lg">
          A print-ready PDF for the kitchen counter, and a phone-friendly PDF for the market. Every link is signed to you and refreshes automatically each visit.
        </p>
      </header>

      <Divider />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        {renderDownloads(loading, downloads)}
      </section>
    </div>
  );
}
